// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	type Action,
	createStore,
	createHook,
	createActionsHook,
	createStateHook,
	createContainer,
} from '@atlassian/react-sweet-state';
import { actions, type Actions } from './actions.tsx';
import {
	type FormSubmitStateContainerProps,
	type SubmitInProgressData,
	type SubmitErrorData,
	SubmitFlagStatus,
	type FormSubmitState,
	type IssueUpdateFlagData,
} from './types.tsx';

const initialState: FormSubmitState = {
	flagStatus: SubmitFlagStatus.NoFlag,
	flagData: undefined,
	data: undefined,
	callbacks: {},
	issueIds: [],
};

const Store = createStore<FormSubmitState, Actions>({
	initialState,
	actions,
	name: 'bulk-operations-form-submit-state',
});

export const useBulkOperationsFormSubmissionState = createHook(Store);
export const hydrateContainer: () => Action<FormSubmitState, FormSubmitStateContainerProps> =
	() =>
	({ setState }, { selectedIssueIds, ...callbacks }: FormSubmitStateContainerProps) => {
		setState({
			callbacks,
			issueIds: selectedIssueIds,
		});
	};

export const FormSubmissionStatusContainer = createContainer<
	FormSubmitState,
	typeof actions,
	FormSubmitStateContainerProps
>(Store, {
	onInit: hydrateContainer,
	onUpdate: hydrateContainer,
});

export const useBulkOperationsFormSubmissionActions = createActionsHook<
	FormSubmitState,
	typeof actions
>(Store);

const getFormSubmitStatus = (state: FormSubmitState): SubmitFlagStatus => state.flagStatus;

export const useFormSubmitStatus = createStateHook(Store, {
	selector: getFormSubmitStatus,
});

const getFormSubmitProgressData = (state: FormSubmitState): SubmitInProgressData | undefined => {
	if (
		state.flagStatus === SubmitFlagStatus.ProgressFlag ||
		state.flagStatus === SubmitFlagStatus.NoFlag
	) {
		return state.data;
	}
	return undefined;
};

export const useFormSubmitProgressData = createStateHook(Store, {
	selector: getFormSubmitProgressData,
});

const getFormSubmitSuccessData = (state: FormSubmitState): number | undefined => {
	if (state.flagStatus === SubmitFlagStatus.SuccessFlag) {
		return state.data.numberOfIssuesUpdated;
	}
	return undefined;
};

export const useFormSubmitSuccessData = createStateHook(Store, {
	selector: getFormSubmitSuccessData,
});

const getFormSubmitIssueIds = (state: FormSubmitState): Array<number> => state.issueIds;

export const useFormSubmitIssueIds = createStateHook(Store, {
	selector: getFormSubmitIssueIds,
});

const getFormSubmitErrorData = (state: FormSubmitState): SubmitErrorData | undefined => {
	if (state.flagStatus === SubmitFlagStatus.PartialErrorFlag) {
		return state.data;
	}
	return undefined;
};

export const useFormSubmitErrorData = createStateHook(Store, {
	selector: getFormSubmitErrorData,
});

const getFormSubmitFlagData = (state: FormSubmitState): IssueUpdateFlagData | undefined =>
	state.flagData;

export const useFormSubmitFlagData = createStateHook(Store, {
	selector: getFormSubmitFlagData,
});
